<script lang="ts">
import type { InputDateProps, InputTimeProps } from '@nuxt/ui';
import type { DatetimeFieldOptions, PrimitiveType } from '#layers/data-model/public';
import { getDatetimePrimitiveSemantics, isEpochIntegerPrimitiveTypeName, PRIMITIVE_TYPE_DATE_TIME, PRIMITIVE_TYPE_DATE_TIME_WITH_TIMEZONE, resolveDatetimeTimezone } from '#layers/data-model/public';

export const supportedDateTimeFieldTypes = ({ primitiveType }: { primitiveType: PrimitiveType }) => {
	const isNativeDatetime = [
		PRIMITIVE_TYPE_DATE_TIME,
		PRIMITIVE_TYPE_DATE_TIME_WITH_TIMEZONE,
	].includes(primitiveType.name);

	return isNativeDatetime || isEpochIntegerPrimitiveTypeName(primitiveType.name)
		? ['datetime']
		: [];
};

const __DsBlock = {
	icon: 'lucide:calendar-clock',
	name: '$t:blocks.input_datetime.name',
	description: '$t:blocks.input_datetime.description',
	type: 'input',
	category: 'input',
	tags: ['form-control'],
	fieldBinding: {
		supportedTypes: supportedDateTimeFieldTypes,
		recommendedDisplays: ['$builtin:DisplayFormattedDateTime'],
	},

	options: [
		{
			name: 'modelValue',
			label: '$t:blocks.common.props.value',
			model: true,
		},
		{
			name: 'granularity',
			label: '$t:blocks.input_datetime.props.granularity',
			type: 'enum',
			values: ['hour', 'minute', 'second'],
		},
		{
			name: 'defaultTimezone',
			label: '$t:blocks.common.props.default_timezone',
		},
		{
			name: 'variant',
			label: '$t:blocks.common.props.variant',
			type: 'enum',
			values: BLOCK_INPUT_VARIANT_OPTIONS,
		},
		{
			name: 'size',
			label: '$t:blocks.common.props.size',
			type: 'enum',
			values: BLOCK_CONTROL_SIZE_OPTIONS,
		},
		{
			name: 'disabled',
			label: '$t:blocks.common.props.disabled',
		},
		{
			name: 'readonly',
			label: '$t:blocks.common.props.readonly',
		},
		{
			name: 'disabledNotice',
			label: '$t:blocks.common.props.disabled_notice',
		},
	],
}</script>

<script lang="ts" setup>
import type { Temporal } from 'temporal-polyfill';
import type { InputBlockProps } from '#layers/blocks/types/input-block';
import { useLocale } from '@nuxt/ui/composables';
import { reactivePick } from '@vueuse/core';
import { computed } from 'vue';
import { calendarDateTimeConverter, isEpochTimestampValue, isZonedDateTime, nowDateTime, nowInTimezone, unixTimestampConverter } from '#layers/base/public';
import { defineBlock } from '#layers/block-engine/public';
import { useTimezone } from '#layers/blocks/composables/use-timezone';
import { BLOCK_CONTROL_SIZE_OPTIONS, BLOCK_INPUT_VARIANT_OPTIONS } from '#layers/blocks/constants/ui-options';
import { usePrimitiveFormField } from '#layers/forms/public';
import { useDisabledNotice } from '#layers/ui/public';

export type DateTimeModelValue = Temporal.PlainDateTime | Temporal.ZonedDateTime | number | string | null;

interface Props extends DatetimeFieldOptions, InputBlockProps, Pick<InputDateProps, 'variant' | 'size' | 'disabled'>, Pick<InputTimeProps, 'granularity'> {
	block?: boolean;
	// Absorbs the shared placeholder ("NULL") DefaultValueInput passes to every input block; datetime inputs don't render it.
	placeholder?: string;
}

const props = withDefaults(defineProps<Props>(), {
	block: true,
	granularity: 'minute',
	defaultTimezone: 'local',
});

const model = defineModel<DateTimeModelValue>();

;

const forwarded = reactivePick(props, 'variant', 'size', 'disabled', 'block', 'granularity', 'readonly');

const { modelValue: m, disabled, disabledNotice, field } = usePrimitiveFormField(props, { model });
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });

const datetimeSemantics = computed(() => getDatetimePrimitiveSemantics(field.value?.type.name));
const isTimestamp = computed(() => datetimeSemantics.value.isTimestamp);
const isTimezoneType = computed(() => datetimeSemantics.value.isTimezoneType);
const isInstantType = computed(() => datetimeSemantics.value.isInstantType);

/**
 * Current timezone for the datetime value.
 * - For timezone-aware and epoch timestamp types: uses defaultTimezone (defaults to user's local timezone)
 * - For other types: always uses 'UTC' for consistency
 */
const currentTimezone = computed(() => resolveDatetimeTimezone(isInstantType.value, props.defaultTimezone));

const internalModel = computed<Temporal.PlainDateTime | null>({
	get: () => {
		if (m.value == null) return null;

		if (isTimestamp.value && isEpochTimestampValue(m.value)) {
			return unixTimestampConverter.toPlainDateTime(m.value, currentTimezone.value);
		}

		if (isZonedDateTime(m.value)) {
			return m.value.withTimeZone(currentTimezone.value).toPlainDateTime();
		}

		return m.value as Temporal.PlainDateTime;
	},
	set: (value) => {
		if (!value) {
			m.value = null;
			return;
		}

		if (isTimestamp.value) {
			m.value = unixTimestampConverter.fromPlainDateTime(value, currentTimezone.value);
			return;
		}

		if (isTimezoneType.value) {
			m.value = value.toZonedDateTime(currentTimezone.value);
			return;
		}

		m.value = value;
	},
});

const { code: locale } = useLocale();

const {
	timezoneLabel,
	tooltipContent,
} = useTimezone({
	timezone: currentTimezone,
	dateTime: internalModel,
	locale,
});

const calendarModel = computed({
	get: () => calendarDateTimeConverter.toCalendar(internalModel.value),
	set: (value) => {
		internalModel.value = calendarDateTimeConverter.toTemporal(value);
	},
});

function setNow() {
	if (isInstantType.value) {
		const now = nowInTimezone(currentTimezone.value);
		internalModel.value = now.toPlainDateTime();
	}
	else {
		internalModel.value = nowDateTime();
	}
}
</script>

<template>
	<DFieldGroup class="w-full">
		<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
			<DInputDate
				v-bind="forwarded"
				v-model="calendarModel"
				class="min-w-0 flex-1 gap-0.5 overflow-hidden"
				:aria-readonly="readonly"
			>
				<template #trailing>
					<DPopover
						:content="{ side: 'bottom', align: 'end' }"
						:ui="{
							content: 'p-2',
						}"
					>
						<DButton
							v-if="!readonly"
							color="neutral"
							variant="link"
							data-testid="input-date-time-calendar"
							icon="lucide:calendar"
							:disabled
						/>

						<template #content>
							<DCalendar v-model="calendarModel" />
							<div class="flex flex-col items-center justify-center">
								<DInputTime v-model="calendarModel" :granularity :readonly />
								<DSeparator class="my-2" />
								<DButton
									v-if="!readonly"
									variant="link"
									data-testid="input-date-time-now"
									@click="setNow"
								>
									{{ $t('blocks.input_date.now') }}
								</DButton>
							</div>
						</template>
					</DPopover>
				</template>
			</DInputDate>
		</DTooltip>

		<!-- Timezone indicator badge with timezone conversion tooltip -->
		<DTooltip
			v-if="isTimezoneType && tooltipContent"
			:delay-duration="0"
			:text="tooltipContent"
		>
			<DBadge
				color="neutral"
				class="cursor-default"
				data-testid="input-date-time-badge"
				variant="subtle"
				icon="lucide:globe"
			>
				{{ timezoneLabel }}
			</DBadge>
		</DTooltip>
	</DFieldGroup>
</template>
